import { useQueryClient } from "@tanstack/react-query";
import type { Order, OrderWithDetails } from "@estelle/domain";

/**
 * What the order edits do once a write comes back (#164).
 *
 * `accepted` takes the order the API answered with — Shopify's response,
 * written to the mirror — and puts it straight into the open detail, then
 * refetches everything order-shaped. Never the form's values: what is on
 * screen after a save is what Shopify returned. Taking the answer at once
 * rather than waiting for the refetch also moves `channelUpdatedAt` forward
 * immediately, so the next edit on the same screen is guarded by the order
 * as it now stands rather than refused as a conflict with the user's own
 * last change.
 */
export function useOrderWrite(orderId: string) {
  const queryClient = useQueryClient();

  return {
    accepted: (order: Order) => {
      queryClient.setQueryData<OrderWithDetails | null>(
        ["orders", "detail", orderId],
        (current) => (current ? { ...current, ...order } : current),
      );
      void queryClient.invalidateQueries({ queryKey: ["orders"] });
      // A cancelled order closes its escalations (#702).
      void queryClient.invalidateQueries({ queryKey: ["escalations"] });
    },
    reload: () => queryClient.invalidateQueries({ queryKey: ["orders"] }),
  };
}
